import { auth, type AuthUser, type ManagedCompany } from 'frontend/inertia/stores/auth.svelte'
import { readInertiaConfig } from 'frontend/inertia/config'
import { learnSessionsPath } from 'frontend/inertia/pages/learn_sessions/routes'
import { usersPath } from 'frontend/inertia/pages/academy_partner/users/routes'
import { groupsPath } from 'frontend/inertia/pages/academy_partner/groups/routes'
import { learningPlansPath } from 'frontend/inertia/pages/academy_partner/learning_plans/routes'
import { learningStatisticsPath } from 'frontend/inertia/pages/academy_partner/learning_statistics/routes'
import { settingsPath } from 'frontend/inertia/pages/academy_partner/settings/routes'
import {
  accountPath,
  adminPath,
  registrationPath,
  sessionPath,
  userHomePath,
  userListsPath,
} from './user_menu_routes'
import { staticPagePath } from 'frontend/inertia/components/footer_routes'
import { userHomeHub } from 'frontend/inertia/stores/user_home_hub.svelte'
import { isNativeApp } from 'shared/javascripts/util/detection'
import { gdprSettingsAvailable } from 'frontend/shared/javascripts/util/gdpr_consent'

export interface AuthMenuChild {
  icon: string
  label: string
  href?: string
  // Wildcard patterns for pages below `href` that should still mark this entry as current.
  currentPaths?: string[]
}

export interface AuthMenuItem extends Omit<AuthMenuChild, 'href'> {
  href: string
  target?: '_blank'
  method?: 'delete'
  children?: AuthMenuChild[]
  key?: 'login' | 'register'
  returnTo?: boolean
}

export interface AuthMenuAction {
  icon: string
  label: string
  action: 'gdprSettings' | 'startIntro'
}

export type UserMenuEntry = AuthMenuItem | AuthMenuAction

export interface UserMenuOptions {
  studybuddyMenuVisible: boolean
  feedbackUrl: string
  userHomeHubVariation: boolean
  nativeApp: boolean
  gdprSettings: boolean
}

export function userMenuOptions(): UserMenuOptions {
  return {
    studybuddyMenuVisible: auth.studybuddyMenuVisible,
    feedbackUrl: readInertiaConfig().feedbackUrl,
    userHomeHubVariation: userHomeHub.variation,
    nativeApp: isNativeApp(),
    gdprSettings: gdprSettingsAvailable(),
  }
}

export function isMenuLink(entry: UserMenuEntry): entry is AuthMenuItem {
  return 'href' in entry
}

export function headerLink(
  entries: UserMenuEntry[],
  key: NonNullable<AuthMenuItem['key']>,
): AuthMenuItem | undefined {
  return entries.filter(isMenuLink).find((entry) => entry.key === key)
}

export function excludeHeaderLinks(entries: UserMenuEntry[]): UserMenuEntry[] {
  return entries.filter((entry) => !isMenuLink(entry) || !entry.key)
}

export function withReturnTo(entries: UserMenuEntry[], currentPath: string): UserMenuEntry[] {
  return entries.map((entry) => {
    if (!isMenuLink(entry) || !entry.returnTo) return entry

    const glue = entry.href.includes('?') ? '&' : '?'
    return { ...entry, href: `${entry.href}${glue}return_to=${currentPath}` }
  })
}

export function signedInMenuItems(user: AuthUser, options: UserMenuOptions): UserMenuEntry[] {
  const items: UserMenuEntry[] = []

  if (user.academyRole) {
    items.push(academyPartnerItem(user.academyRole))
  }

  if (user.assignedLearningPlans) {
    items.push(section('lernplaene', 'Zugewiesene Lernpläne', learningStatisticsPath.show(user.id)))
  }

  if (options.studybuddyMenuVisible) {
    items.push(learnChatsItem())
  }

  items.push(myContentsItem(user, options))

  if (!user.customerSideAdministrator || options.nativeApp) {
    items.push({
      icon: 'change-password',
      label: 'Profil bearbeiten',
      href: registrationPath.edit(),
    })
  }

  if (user.adminDashboards && !options.nativeApp) {
    items.push({ icon: 'verified-user', label: 'Admin', href: adminPath.root() })
  }

  if (user.customerSideAdministrator && !options.nativeApp) {
    if (user.managedCompany) {
      items.push({
        icon: 'enterprise',
        label: 'Unternehmen verwalten',
        href: managedCompanyHref(user.managedCompany),
      })
    }
    items.push({ icon: 'change-password', label: 'Passwort ändern', href: accountPath.edit() })
  }

  items.push(...closingItems(options))
  items.push({ icon: 'logout', label: 'Logout', href: sessionPath.destroy(), method: 'delete' })

  return items
}

export function signedOutMenuItems(options: UserMenuOptions): UserMenuEntry[] {
  const items: UserMenuEntry[] = [
    { key: 'login', returnTo: true, icon: 'profile', label: 'Login', href: sessionPath.new() },
    {
      key: 'register',
      returnTo: true,
      icon: 'edit',
      label: 'Registrieren',
      // The native app still asks for education data before the sign up.
      href: options.nativeApp ? registrationPath.educationInfo() : registrationPath.new(),
    },
  ]

  if (options.studybuddyMenuVisible) {
    items.push(learnChatsItem())
  }

  items.push(...closingItems(options))

  return items
}

function closingItems(options: UserMenuOptions): UserMenuEntry[] {
  const feedback = feedbackItem(options.feedbackUrl)

  if (!options.nativeApp) {
    return [feedback]
  }

  const items: UserMenuEntry[] = [
    { icon: 'file', label: 'Impressum', href: staticPagePath.imprint() },
    { icon: 'file', label: 'AGB', href: staticPagePath.terms() },
    { icon: 'file', label: 'Datenschutz', href: staticPagePath.privacy() },
  ]

  if (options.gdprSettings) {
    items.push({ icon: 'file', label: 'Datenschutz-Einstellungen', action: 'gdprSettings' })
  }

  items.push(
    feedback,
    { icon: 'repeat', label: 'Einführung nochmal ansehen', action: 'startIntro' },
    { icon: 'about', label: 'Über Studyflix', href: staticPagePath.aboutStudyflix() },
  )

  return items
}

function academyPartnerItem(role: 'admin' | 'trainer'): AuthMenuItem {
  const learningPlanSections = [
    section('about', 'Lernstatistiken', learningStatisticsPath.index()),
    section('lernplaene', 'Lernplanverwaltung', learningPlansPath.index()),
  ]

  if (role === 'admin') {
    return section('events', 'Nutzerverwaltung', usersPath.index(), [
      section('content-overview', 'Nutzerverwaltung', usersPath.index()),
      section('themen', 'Gruppen', groupsPath.index()),
      ...learningPlanSections,
      section('settings', 'Einstellungen', settingsPath.edit()),
    ])
  }

  return section('events', 'Meine Lernenden', learningPlansPath.index(), learningPlanSections)
}

function section(
  icon: string,
  label: string,
  href: string,
  children?: AuthMenuChild[],
): AuthMenuItem {
  return { icon, label, href, currentPaths: [`${href}/*`], ...(children && { children }) }
}

function myContentsItem(user: AuthUser, options: UserMenuOptions): AuthMenuItem {
  const lists: AuthMenuChild[] = [
    {
      icon: 'lernplaene',
      label: user.academyUser ? 'Private Lernpläne' : 'Lernpläne',
      href: userListsPath.customPlaylists(),
    },
    { icon: 'heart-outline', label: 'Favoriten', href: userListsPath.bookmarks() },
    { icon: 'playlist', label: 'Verlauf', href: userListsPath.history() },
  ]

  // The user home of the A/B test user_home_hub links to the lists itself, so the entry opens no submenu.
  if (user.userHomeAvailable && options.userHomeHubVariation) {
    return {
      icon: 'inhalte',
      label: 'Meine Inhalte',
      href: userHomePath.show(),
      currentPaths: lists.map((list) => list.href!),
    }
  }

  return {
    icon: 'inhalte',
    label: 'Meine Inhalte',
    href: userListsPath.customPlaylists(),
    children: lists,
  }
}

export function learnChatsItem(): AuthMenuItem {
  return { icon: 'lightbulb', label: 'Meine Lern-Chats', href: learnSessionsPath.index() }
}

export function feedbackItem(feedbackUrl: string): AuthMenuItem {
  return { icon: 'chats', label: 'Feedback', href: feedbackUrl, target: '_blank' }
}

// A company administrator with several companies lands on the index instead.
export function managedCompanyHref(company: ManagedCompany): string {
  return company.kind === 'single'
    ? adminPath.companyDashboard(company.companyId)
    : adminPath.companies()
}
